import { useMemo } from 'react';

import {
  type AtlasGqlPotentialDamageAttributes,
  useGetPotentialDamageAttributesQuery,
} from 'types/atlas-graphql';

interface Props {
  pictureId?: string;
  coordinates?: number[];
  attrs?: Record<string, any>;
  includeAssets?: boolean;
}

export function usePotentialDamageAttrs({
  pictureId,
  coordinates,
  attrs,
  includeAssets = false,
}: Props) {
  const { data, loading } = useGetPotentialDamageAttributesQuery({
    variables: {
      pictureId,
      coordinates,
      attrs,
      includeAssets,
    },
    skip: !pictureId || !coordinates || coordinates.length < 4,
  });
  const {
    // generic attributes
    inspectionDate,
    length,
    width,

    // blade attributes
    radialDistance,
    bladeSide,
    bladeChamber,
    chordAverage,
    chordPoints,

    // tower/tp attributes
    distanceFromNacelleCenter,
    towerSide,
    transitionPieceSide,

    // nacelle attributes
    nacelleSide,

    // assets
    assets,
  } = (data?.getPotentialDamageAttributes ?? {}) as AtlasGqlPotentialDamageAttributes;

  const potentialAttrs = useMemo(
    () => ({
      // generic attributes
      Date: inspectionDate,
      Length: length,
      Width: width,

      // blade attributes
      Distance: radialDistance,
      'Blade Side': bladeSide,
      'Blade Chamber': bladeChamber,
      Chord: chordAverage,
      'Chord Points': chordPoints ? JSON.stringify(chordPoints) : chordPoints,

      // tower/tp attributes
      'Distance from Nacelle': distanceFromNacelleCenter,
      'Tower Side': towerSide,
      'Transition Piece Side': transitionPieceSide,

      // nacelle attributes
      'Nacelle Side': nacelleSide,

      // assets
      Assets: assets ?? [],
    }),
    [
      inspectionDate,
      length,
      width,
      radialDistance,
      bladeSide,
      bladeChamber,
      chordAverage,
      chordPoints,
      distanceFromNacelleCenter,
      towerSide,
      transitionPieceSide,
      nacelleSide,
      assets,
    ]
  );

  return { loading, potentialAttrs };
}
